import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { TopBar } from "@/components/TopBar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileDown, Plus, Trash2, Pencil } from "lucide-react";

const KINDS = [
  { kind: "treatment", label: "treatment" },
  { kind: "material", label: "material" },
  { kind: "report", label: "reports" },
];

export default function HorseFile() {
  const { t, user } = useApp();
  const { id } = useParams();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [horse, setHorse] = useState(null);
  const [entries, setEntries] = useState([]);
  const [tab, setTab] = useState("treatment");
  const [form, setForm] = useState({ title: "", date: "", description: "" });

  const load = () => {
    api.get(`/horses/${id}`).then((r) => setHorse(r.data)).catch(() => nav(-1));
    api.get(`/horses/${id}/entries`).then((r) => setEntries(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  const addEntry = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/horses/${id}/entries`, { kind: tab, ...form });
      setForm({ title: "", date: "", description: "" });
      toast.success(t("t_saved"));
      load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || t("t_error"));
    }
  };

  const delEntry = async (eid) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/entries/${eid}`);
    toast.success(t("t_deleted"));
    load();
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/horses/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `akte_${horse?.name || "pferd"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t("t_error"));
    }
  };

  if (!horse) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">…</div>;

  const stat = (label, val) => (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-foreground">{val || "—"}</div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <TopBar showBack backTo={isAdmin ? "/admin" : "/customer"} showLogout />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <div className="rounded-[20px] border border-border bg-card p-6 shadow-[0_8px_30px_rgba(62,97,72,0.06)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-serif-h text-3xl text-foreground" data-testid="horse-name">{horse.name}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{[horse.breed, horse.age, horse.color].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="flex gap-2">
              {isAdmin && (
                <Button data-testid="edit-horse" variant="outline" className="rounded-full" onClick={() => nav(`/horse-form/${id}`)}>
                  <Pencil className="mr-2 h-4 w-4" strokeWidth={1.5} />{t("edit")}
                </Button>
              )}
              <Button data-testid="export-pdf" onClick={exportPdf} className="rounded-full bg-primary text-primary-foreground hover:bg-[#2C4633]">
                <FileDown className="mr-2 h-4 w-4" strokeWidth={1.5} />{t("export_pdf")}
              </Button>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-3">
            {stat(t("owner"), horse.owner_name)}
            {stat(t("email"), horse.owner_email)}
            {stat(t("phone"), horse.owner_phone)}
          </div>
          {horse.notes && (
            <div className="mt-5 rounded-[12px] bg-[#FBF3E7] p-4 text-sm text-[#1A2C21]">{horse.notes}</div>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="rounded-full bg-[#EAF0EB] p-1">
            {KINDS.map((k) => (
              <TabsTrigger key={k.kind} value={k.kind} data-testid={`tab-${k.kind}`} className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {t(k.label)}
              </TabsTrigger>
            ))}
          </TabsList>

          {KINDS.map((k) => (
            <TabsContent key={k.kind} value={k.kind} className="mt-6 space-y-4">
              {entries.filter((e) => e.kind === k.kind).length === 0 && (
                <p className="text-muted-foreground" data-testid="no-entries">{t("no_entries")}</p>
              )}
              {entries.filter((e) => e.kind === k.kind).map((e) => (
                <div key={e.id} data-testid={`entry-${e.id}`} className="flex items-start justify-between gap-4 rounded-[12px] border border-border bg-card p-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-foreground">{e.title}</span>
                      <span className="text-xs text-muted-foreground">{e.date}</span>
                    </div>
                    {e.description && <p className="mt-1 text-sm text-muted-foreground">{e.description}</p>}
                  </div>
                  {isAdmin && (
                    <button data-testid={`delete-entry-${e.id}`} onClick={() => delEntry(e.id)} className="text-destructive hover:opacity-70">
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              ))}

              {isAdmin && (
                <form onSubmit={addEntry} className="rounded-[12px] border border-dashed border-border bg-card p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="mb-1.5 block text-sm">{t("entry_title")}</Label>
                      <Input data-testid="entry-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="rounded-[12px]" />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm">{t("entry_date")}</Label>
                      <Input data-testid="entry-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-[12px]" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="mb-1.5 block text-sm">{t("entry_desc")}</Label>
                    <Textarea data-testid="entry-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-[12px]" />
                  </div>
                  <Button type="submit" data-testid="add-entry" className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-[#2C4633]">
                    <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />{t("add_entry")}
                  </Button>
                </form>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
